// Phase 12 — Manager BTL/Display Verification: list + review + score/verify/reject
import AuthImage from "@/components/AuthImage";
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { ArrowLeft, CheckCircle2, XCircle, Award, Camera, FileText } from "lucide-react";
import { api, API_BASE, formatApiError } from "@/lib/api";
import { getToken, photoRefPath } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Slider } from "@/components/ui/slider";
import PageHeader from "@/components/PageHeader";

const CATEGORIES = [
  { code: "", label: "All BTL / Display" },
  { code: "btl", label: "BTL Activity" },
  { code: "display", label: "Display" },
];

export default function ActivitySubmissionsList() {
  const [items, setItems] = useState([]);
  const [category, setCategory] = useState("btl");
  const [status, setStatus] = useState("submitted");
  const [reviewId, setReviewId] = useState(null);
  const [loading, setLoading] = useState(true);

  async function load() {
    setLoading(true);
    try {
      const params = { page_size: 50 };
      if (category) params.category = category;
      if (status) params.status = status;
      const { data } = await api.get("/activity-submissions", { params });
      setItems(data.items || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }
  useEffect(() => { load(); }, [category, status]);

  return (
    <div className="max-w-5xl mx-auto p-4" data-testid="manager-activity-list">
      <PageHeader
        breadcrumbs={[{ label: "Admin", to: "/" }, { label: "Field ops" }, { label: "Verify activities" }]}
        title="Verify activities"
        subtitle="Manager review of field-submitted activity forms."
        favorite={{ key: "admin.verify-activities", type: "screen", label: "Verify activities", route: "/manager/activity-submissions" }}
        testid="activitysubmissionslist-page-header"
      />

      <div className="mb-4">
        <h1 className="text-xl font-semibold">Activity Verification</h1>
        <p className="text-sm text-neutral-500">Review, score and verify BTL / Display submissions from the field.</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
        <div>
          <Label>Category</Label>
          <Select value={category || "__all__"} onValueChange={(v) => setCategory(v === "__all__" ? "" : v)}>
            <SelectTrigger data-testid="mgr-cat"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">All BTL / Display</SelectItem>
              <SelectItem value="btl">BTL Activity</SelectItem>
              <SelectItem value="display">Display</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Status</Label>
          <Select value={status || "__all__"} onValueChange={(v) => setStatus(v === "__all__" ? "" : v)}>
            <SelectTrigger data-testid="mgr-status"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">Any</SelectItem>
              <SelectItem value="submitted">Submitted</SelectItem>
              <SelectItem value="verified">Verified</SelectItem>
              <SelectItem value="rejected">Rejected</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="bg-white border rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-neutral-50 text-left text-neutral-600">
            <tr>
              <th className="px-4 py-3 font-medium">Form</th>
              <th className="px-4 py-3 font-medium">Outlet</th>
              <th className="px-4 py-3 font-medium">Submitter</th>
              <th className="px-4 py-3 font-medium">Submitted</th>
              <th className="px-4 py-3 font-medium">Score</th>
              <th className="px-4 py-3 font-medium">Status</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={7} className="px-4 py-8 text-center text-neutral-500">Loading…</td></tr>}
            {!loading && items.length === 0 && (
              <tr><td colSpan={7} className="px-4 py-8 text-center text-neutral-400">No submissions in this filter.</td></tr>
            )}
            {items.map(s => (
              <tr key={s.id} className="border-t hover:bg-neutral-50" data-testid={`sub-row-${s.id.slice(0,8)}`}>
                <td className="px-4 py-3">
                  <div className="font-medium">{s.form_name_snapshot || s.form_code_snapshot}</div>
                  <div className="text-[11px] text-neutral-400 font-mono">{s.form_code_snapshot}</div>
                </td>
                <td className="px-4 py-3">{s.retailer_name || <span className="text-neutral-400">—</span>}</td>
                <td className="px-4 py-3">{s.user_name}</td>
                <td className="px-4 py-3 text-xs">{new Date(s.submission_ts).toLocaleString()}</td>
                <td className="px-4 py-3">
                  {s.compliance_score != null ? (
                    <span className={`font-semibold ${s.compliance_score >= 60 ? "text-emerald-700" : "text-amber-700"}`}>
                      {s.compliance_score}
                    </span>
                  ) : <span className="text-neutral-400">—</span>}
                </td>
                <td className="px-4 py-3">
                  <Badge variant="outline" className={
                    s.status === "verified" ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                    : s.status === "rejected" ? "bg-red-50 text-red-700 border-red-200"
                    : "bg-amber-50 text-amber-700 border-amber-200"
                  }>{s.status}</Badge>
                </td>
                <td className="px-4 py-3 text-right">
                  <Button size="sm" variant="outline" onClick={() => setReviewId(s.id)}
                          data-testid={`review-${s.id.slice(0,8)}`}>Review</Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {reviewId && (
        <ReviewDialog submissionId={reviewId} onClose={() => { setReviewId(null); load(); }} />
      )}
    </div>
  );
}


function photoUrl(ref) {
  if (!ref) return null;
  const t = getToken();
  const suffix = t ? `?token=${encodeURIComponent(t)}` : "";
  return `${API_BASE}/photos/${photoRefPath(ref)}${suffix}`;
}


function ReviewDialog({ submissionId, onClose }) {
  const [sub, setSub] = useState(null);
  const [score, setScore] = useState(70);
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [rejectMode, setRejectMode] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/activity-submissions/${submissionId}`);
        setSub(data);
        if (data.compliance_score != null) setScore(Number(data.compliance_score));
      } catch (e) { toast.error(formatApiError(e)); onClose(); }
    })();
  }, [submissionId]);

  async function doScore() {
    setBusy(true);
    try {
      await api.post(`/activity-submissions/${submissionId}/score`, {
        compliance_score: score,
      });
      toast.success(`Scored: ${score}`);
      const { data } = await api.get(`/activity-submissions/${submissionId}`);
      setSub(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  }

  async function doVerify() {
    setBusy(true);
    try {
      // score first if changed
      if (sub.compliance_score !== score) {
        await api.post(`/activity-submissions/${submissionId}/score`, {
          compliance_score: score,
        });
      }
      await api.post(`/activity-submissions/${submissionId}/verify`, { reviewer_notes: notes || null });
      toast.success("Verified ✓");
      onClose();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  }

  async function doReject() {
    if (!notes) { toast.error("Rejection reason required"); return; }
    setBusy(true);
    try {
      await api.post(`/activity-submissions/${submissionId}/reject`, { reviewer_notes: notes });
      toast.success("Rejected");
      onClose();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  }

  if (!sub) return null;

  const photoPairField = (sub.fields || []).find(f => f.field_type === "photo_pair");
  const pairVal = photoPairField ? (sub.values || {})[photoPairField.field_key] || {} : {};

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-3xl" data-testid="review-dialog">
        <DialogHeader>
          <DialogTitle>{sub.form_name_snapshot}</DialogTitle>
          <DialogDescription>
            {sub.retailer_name || "—"} · {sub.user_name} · {new Date(sub.submission_ts).toLocaleString()}
          </DialogDescription>
        </DialogHeader>

        {photoPairField && (
          <div className="grid grid-cols-2 gap-3" data-testid="review-photo-pair">
            <div>
              <Label className="text-xs uppercase tracking-wide text-neutral-500">Before</Label>
              {pairVal.before ? (
                <AuthImage refId={pairVal.before} alt="before"
                     className="w-full h-56 object-cover rounded-md border mt-1" />
              ) : <div className="h-56 border-2 border-dashed rounded-md flex items-center justify-center text-neutral-400">No before</div>}
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wide text-neutral-500">After</Label>
              {pairVal.after ? (
                <AuthImage refId={pairVal.after} alt="after"
                     className="w-full h-56 object-cover rounded-md border mt-1" />
              ) : <div className="h-56 border-2 border-dashed rounded-md flex items-center justify-center text-neutral-400">No after</div>}
            </div>
          </div>
        )}

        <div className="bg-neutral-50 rounded-md p-3">
          <div className="text-xs uppercase tracking-wide text-neutral-500 mb-2">Submitted values</div>
          <div className="grid grid-cols-2 gap-2 text-sm">
            {(sub.fields || []).filter(f => f.field_type !== "section_divider" && f.field_type !== "photo_pair").map(f => {
              const v = (sub.values || {})[f.field_key];
              if (v == null || v === "") return null;
              return (
                <div key={f.id} className="min-w-0">
                  <div className="text-[11px] text-neutral-500">{f.label}</div>
                  <div className="truncate">{typeof v === "object" ? JSON.stringify(v) : String(v)}</div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="border-t pt-3 space-y-3">
          <div>
            <div className="flex items-center justify-between mb-1">
              <Label>Compliance score</Label>
              <span className={`text-2xl font-bold ${score >= 60 ? "text-emerald-600" : "text-amber-600"}`}>{score}</span>
            </div>
            <Slider min={0} max={100} step={1} value={[score]}
                    onValueChange={([v]) => setScore(v)} data-testid="review-score-slider" />
          </div>
          <div>
            <Label>Reviewer notes {rejectMode && <span className="text-red-500">*</span>}</Label>
            <Textarea data-testid="review-notes" value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} />
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose}>Close</Button>
          <Button variant="outline" onClick={doScore} disabled={busy} data-testid="review-save-score">
            <Award className="w-4 h-4 mr-1" /> Save score
          </Button>
          <Button variant="outline" className="text-red-600 border-red-200" onClick={doReject} disabled={busy}
                  data-testid="review-reject">
            <XCircle className="w-4 h-4 mr-1" /> Reject
          </Button>
          <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={doVerify} disabled={busy}
                  data-testid="review-verify">
            <CheckCircle2 className="w-4 h-4 mr-1" /> Verify
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
